import React from "react";

/**
 * Expanded edit panel — shown below the action bar when "Edit" is toggled.
 * Contains: regen post, full regenerate, main image editing, secondary image editing, delete.
 */
const BlogEditPanel = ({
  busy,
  showRegenForm,
  setShowRegenForm,
  regenFeedback,
  setRegenFeedback,
  onRegenerate,
  onFullRegenerate,
  // Main image
  showImgForm,
  setShowImgForm,
  imgFormMode,
  setImgFormMode,
  editPrompt,
  setEditPrompt,
  newImgPrompt,
  setNewImgPrompt,
  newImgUseDefault,
  setNewImgUseDefault,
  onEditImage,
  onNewImage,
  // Secondary image
  showSecondaryImgForm,
  setShowSecondaryImgForm,
  secondaryImgFormMode,
  setSecondaryImgFormMode,
  secondaryEditPrompt,
  setSecondaryEditPrompt,
  secondaryNewImgPrompt,
  setSecondaryNewImgPrompt,
  secondaryNewImgUseDefault,
  setSecondaryNewImgUseDefault,
  onEditSecondaryImage,
  onNewSecondaryImage,
  onDelete,
}) => (
  <div className="border-t border-gray-100 bg-gray-50 max-w-[860px] mx-auto px-4 py-4">
    {/* Edit post sub-section */}
    <div className="mb-3 p-4 bg-white border border-amber-200 rounded-lg">
      {!showRegenForm ? (
        <button
          onClick={() => setShowRegenForm(true)}
          className="text-sm text-amber-700 hover:text-amber-900 font-medium"
        >
          ✏️ Edit post…
        </button>
      ) : (
        <>
          <p className="text-sm font-medium text-amber-800 mb-2">Feedback for the AI (optional):</p>
          <textarea
            className="w-full border border-amber-300 rounded p-2 text-sm text-gray-800 mb-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
            rows={2}
            placeholder="e.g. Make it shorter, focus on toddlers…"
            value={regenFeedback}
            onChange={e => setRegenFeedback(e.target.value)}
            style={{ color: "#1a1a2e" }}
          />
          <div className="flex gap-2">
            <button onClick={onRegenerate} className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded hover:bg-amber-700">Apply edits</button>
            <button onClick={() => setShowRegenForm(false)} className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-300">Cancel</button>
          </div>
        </>
      )}
    </div>

    {/* Full regenerate */}
    <div className="mb-3 p-4 bg-white border border-orange-200 rounded-lg">
      <button
        onClick={onFullRegenerate}
        className="text-sm text-orange-700 hover:text-orange-900 font-medium"
      >
        ♻️ Regenerate from scratch…
      </button>
    </div>

    {/* Main image editing */}
    <div className="mb-3 p-4 bg-white border border-purple-200 rounded-lg">
      <p className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-2">Hero image</p>
      {!showImgForm ? (
        <button
          onClick={() => setShowImgForm(true)}
          className="text-sm text-purple-700 hover:text-purple-900 font-medium"
        >
          🖼 Edit hero image…
        </button>
      ) : (
        <>
          <div className="flex gap-1 mb-3">
            <button
              onClick={() => setImgFormMode("edit")}
              className={`px-3 py-1 text-sm rounded-full font-medium transition-colors ${imgFormMode === "edit" ? "bg-purple-600 text-white" : "bg-purple-100 text-purple-700 hover:bg-purple-200"}`}
            >
              ✏️ Edit current image
            </button>
            <button
              onClick={() => setImgFormMode("new")}
              className={`px-3 py-1 text-sm rounded-full font-medium transition-colors ${imgFormMode === "new" ? "bg-purple-600 text-white" : "bg-purple-100 text-purple-700 hover:bg-purple-200"}`}
            >
              🖼 New image
            </button>
          </div>

          {imgFormMode === "edit" ? (
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                className="flex-1 border border-purple-300 rounded p-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-400"
                placeholder="e.g. add more balloons, change background to outdoors…"
                value={editPrompt}
                onChange={e => setEditPrompt(e.target.value)}
                onKeyDown={e => e.key === "Enter" && onEditImage()}
                style={{ color: "#1a1a2e" }}
              />
              <button
                onClick={onEditImage}
                disabled={!editPrompt.trim()}
                className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 disabled:opacity-50 whitespace-nowrap"
              >
                Apply
              </button>
            </div>
          ) : (
            <div className="mb-2">
              <textarea
                className="w-full border border-purple-300 rounded p-2 text-sm text-gray-800 mb-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
                rows={2}
                placeholder="Custom image prompt (leave blank for AI to generate from post content)"
                value={newImgPrompt}
                onChange={e => setNewImgPrompt(e.target.value)}
                style={{ color: "#1a1a2e" }}
              />
              {/* Default style prompt toggle */}
              <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
                <div
                  onClick={() => setNewImgUseDefault(v => !v)}
                  className={`relative w-9 h-5 rounded-full transition-colors ${newImgUseDefault ? "bg-purple-500" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${newImgUseDefault ? "translate-x-4" : "translate-x-0"}`} />
                </div>
                <span className="text-xs text-gray-600">
                  {newImgUseDefault
                    ? <span>Default style <span className="text-gray-400">(Pixar/Disney, post context appended)</span></span>
                    : <span className="text-gray-400">Default style off — prompt sent as-is</span>}
                </span>
              </label>
              <button
                onClick={onNewImage}
                className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700"
              >
                🖼 Generate New Image
              </button>
            </div>
          )}

          <button
            onClick={() => { setShowImgForm(false); setEditPrompt(""); setNewImgPrompt(""); }}
            className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-300"
          >
            Cancel
          </button>
        </>
      )}
    </div>

    {/* Secondary (mid-article) image editing */}
    <div className="mb-3 p-4 bg-white border border-indigo-200 rounded-lg">
      <p className="text-xs font-semibold text-indigo-400 uppercase tracking-wide mb-2">Mid-article image</p>
      {!showSecondaryImgForm ? (
        <button
          onClick={() => setShowSecondaryImgForm(true)}
          className="text-sm text-indigo-700 hover:text-indigo-900 font-medium"
        >
          🖼 Edit mid-article image…
        </button>
      ) : (
        <>
          <div className="flex gap-1 mb-3">
            <button
              onClick={() => setSecondaryImgFormMode("edit")}
              className={`px-3 py-1 text-sm rounded-full font-medium transition-colors ${secondaryImgFormMode === "edit" ? "bg-indigo-600 text-white" : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"}`}
            >
              ✏️ Edit current image
            </button>
            <button
              onClick={() => setSecondaryImgFormMode("new")}
              className={`px-3 py-1 text-sm rounded-full font-medium transition-colors ${secondaryImgFormMode === "new" ? "bg-indigo-600 text-white" : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"}`}
            >
              🖼 New image
            </button>
          </div>

          {secondaryImgFormMode === "edit" ? (
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                className="flex-1 border border-indigo-300 rounded p-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                placeholder="e.g. add more characters, change the mood to playful…"
                value={secondaryEditPrompt}
                onChange={e => setSecondaryEditPrompt(e.target.value)}
                onKeyDown={e => e.key === "Enter" && onEditSecondaryImage()}
                style={{ color: "#1a1a2e" }}
              />
              <button
                onClick={onEditSecondaryImage}
                disabled={!secondaryEditPrompt.trim()}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
              >
                Apply
              </button>
            </div>
          ) : (
            <div className="mb-2">
              <textarea
                className="w-full border border-indigo-300 rounded p-2 text-sm text-gray-800 mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                rows={2}
                placeholder="Custom image prompt (leave blank for AI to generate from post content)"
                value={secondaryNewImgPrompt}
                onChange={e => setSecondaryNewImgPrompt(e.target.value)}
                style={{ color: "#1a1a2e" }}
              />
              {/* Default style prompt toggle */}
              <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
                <div
                  onClick={() => setSecondaryNewImgUseDefault(v => !v)}
                  className={`relative w-9 h-5 rounded-full transition-colors ${secondaryNewImgUseDefault ? "bg-indigo-500" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${secondaryNewImgUseDefault ? "translate-x-4" : "translate-x-0"}`} />
                </div>
                <span className="text-xs text-gray-600">
                  {secondaryNewImgUseDefault
                    ? <span>Default style <span className="text-gray-400">(Pixar/Disney, post context appended)</span></span>
                    : <span className="text-gray-400">Default style off — prompt sent as-is</span>}
                </span>
              </label>
              <button
                onClick={onNewSecondaryImage}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700"
              >
                🖼 Generate New Image
              </button>
            </div>
          )}

          <button
            onClick={() => { setShowSecondaryImgForm(false); setSecondaryEditPrompt(""); setSecondaryNewImgPrompt(""); }}
            className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-300"
          >
            Cancel
          </button>
        </>
      )}
    </div>

    {/* Delete */}
    <button
      onClick={onDelete}
      className="text-sm text-red-600 hover:text-red-800 font-medium"
      disabled={busy}
    >
      🗑 Delete this post
    </button>
  </div>
);

export default BlogEditPanel;
